import InputField from "../../../components/form/input-field"
import SelectField from "../../../components/form/select-field"
import TablePagination from "../../../components/table/table-pagination"
import { BarChart3, CheckCircle, Clock, Eye, Inbox } from "lucide-react"
import moment from "moment"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useSupportTicketOverview } from "../../../composables/support-ticket/useSupportTicketOverview"
import { useTickets } from "../../../composables/support-ticket/useTickets"
import { useDebounced } from "../../../hooks/useDebounce"
import { CustomOptions } from "../../../utils/options"
import { SupportTicket } from "../../../types/ticket-type"
import StatusBadge from "../../../components/ui/StatusBadge"
import { abbreviateString } from "../../../lib/helper-support"
import AuthLayout from "../../../components/layouts/auth"

const SupportTicketDashboard = () => {
    const [page, setCurrentPage] = useState<number>(1);

    const { data: overview } = useSupportTicketOverview({});

    const [search, setSearch] = useState<string>('');
    const [status, setStatus] = useState<string>('');

    const statusDebounced = useDebounced({
        value: status,
    });

    const searchDebounced = useDebounced({
        value: search,
    });

    const { data: tickets } = useTickets({
        page,
        search: searchDebounced,
        status: statusDebounced,
    })

    return (
        <AuthLayout>
            <div className="space-y-6">
                <div>
                    <h1 className="text-[1.5em] font-semibold">Support Dashboard</h1>
                    <p className="themeTextMuted">Manage and resolve customer support tickets</p>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                    <div className="box-shadow p-5 rounded-2xl border border-gray-200 flex justify-between items-center">
                        <div>
                            <label className="themeTextMuted">Total Tickets</label>
                            <h2 className="font-bold text-primary text-2xl">{overview?.total_tickets || 0}</h2>
                        </div>
                        <div className="bg-primary/10 p-2 rounded-lg">
                            <BarChart3 className="w-4 h-4 text-primary" />
                        </div>
                    </div>
                    <div className="box-shadow p-5 rounded-2xl border border-gray-200 flex justify-between items-center">
                        <div>
                            <label className="themeTextMuted">Open</label>
                            <h2 className="font-bold text-primary text-2xl">{overview?.open_tickets || 0}</h2>
                        </div>
                        <div className="bg-primary/10 p-2 rounded-lg">
                            <Inbox className="w-4 h-4 text-primary" />
                        </div>
                    </div>
                    <div className="box-shadow p-5 rounded-2xl border border-gray-200 flex justify-between items-center">
                        <div>
                            <label className="themeTextMuted">In Review</label>
                            <h2 className="font-bold text-yellow-500 text-2xl">{overview?.in_review_tickets || 0}</h2>
                        </div>
                        <div className="bg-yellow-500/10 p-2 rounded-lg">
                            <Clock className="w-4 h-4 text-yellow-500" />
                        </div>
                    </div>
                    <div className="box-shadow p-5 rounded-2xl border border-gray-200 flex justify-between items-center">
                        <div>
                            <label className="themeTextMuted">Resolved</label>
                            <h2 className="font-bold text-green-500 text-2xl">{overview?.closed_tickets || 0}</h2>
                        </div>
                        <div className="bg-green-500/10 p-2 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                    </div>
                </div>

                <div className="box-shadow p-5 rounded-2xl border border-gray-200">
                    <div className="flex justify-between items-center mb-5">
                        <div className="md:w-1/2">
                            <InputField
                                type="search"
                                placeholder="Search tickets"
                                onChangeInput={e => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="md:w-1/4">
                            <SelectField
                                placeholder="All Status"
                                options={CustomOptions(['pending_review', 'in_review', 'awaiting_customer_response', 'closed'])}
                                //required={false}
                                onChangeInput={value => setStatus(value)}
                                value={status}
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto ">
                        <table className="text-secondary font-medium text-[14px] w-full relative z-10 whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-[#E6EAF0] themeTextMuted">
                                    <td className="px-4 py-3">Ticket ID</td>
                                    <td className="px-4 py-3">Customer</td>
                                    <td className="px-4 py-3">Description</td>
                                    <td className="px-4 py-3">Transaction</td>
                                    <td className="px-4 py-3">Status</td>
                                    <td className="px-4 py-3">Created</td>
                                </tr>
                            </thead>
                            <tbody>
                                {tickets?.items?.map((ticket: SupportTicket) => (
                                    <tr key={ticket.code} className="border-b border-[#E6EAF0] relative">
                                        <td className="px-4 py-3">
                                            {ticket.code}
                                            <Link to={`/support-ticket/${ticket.code}`} className='absolute top-0 bottom-0 left-0 w-full h-full hover:bg-[#E6EAF0] opacity-[0.2]' />
                                        </td>
                                        <td className="px-4 py-3">{ticket.customer}</td>
                                        <td className="px-4 py-3">{abbreviateString(ticket.description, 20)}</td>
                                        <td className="px-4 py-3">{ticket.external_reference || '-'}</td>
                                        <td className="px-4 py-3"><StatusBadge status={ticket.status} /></td>
                                        <td className="px-4 py-3">{moment(ticket.created_at).format('h:mm a, MMM D')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className='mt-5'>
                        <TablePagination
                            totalPages={tickets?.totalPages ?? 0}
                            onPageChange={(page) => setCurrentPage(page)}
                            currentPage={tickets?.page}
                        />
                    </div>
                </div>

            </div>
        </AuthLayout>
    )
}

export default SupportTicketDashboard